import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import {
  type ApiError,
  type ApiResponse,
  addActionNameToMutationResult,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { z } from 'zod/v4';
import {
  EXTERNAL_MAILBOX_CONFIGURATIONS_QUERY_KEY,
  EXTERNAL_MAILBOX_CONFIGURATIONS_URL,
} from './use-load-external-mailbox-configurations';

// `login` is deliberately not an email: a shared mailbox is configured as
// `owner@company.com\shared@company.com`, which no email validator accepts.
export const CreateExternalMailboxConfigurationVariablesSchema = z.object({
  host: z.string().min(1),
  port: z.number().int().positive(),
  ssl: z.boolean(),
  login: z.string().min(1),
  password: z.string().min(1),
  signature: z.string(),
});

export type CreateExternalMailboxConfigurationVariables = z.infer<
  typeof CreateExternalMailboxConfigurationVariablesSchema
>;

export async function createExternalMailboxConfigurationFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: CreateExternalMailboxConfigurationVariables,
) {
  const instance = await axiosInstance;

  return instance.post(
    EXTERNAL_MAILBOX_CONFIGURATIONS_URL,
    CreateExternalMailboxConfigurationVariablesSchema.parse(variables),
  );
}

export function useCreateExternalMailboxConfiguration() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, CreateExternalMailboxConfigurationVariables>({
    mutationFn: variables => createExternalMailboxConfigurationFn(axiosInstance, variables),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: EXTERNAL_MAILBOX_CONFIGURATIONS_QUERY_KEY });
    },
  });

  return addActionNameToMutationResult<
    ApiResponse,
    ApiError,
    CreateExternalMailboxConfigurationVariables,
    'createExternalMailboxConfiguration'
  >('createExternalMailboxConfiguration', mutation);
}
